/**
 * WHICH coarse chunk requests to issue when the streaming window has room.
 *
 * The onion's levels are not disjoint: each coarse level's in-range set is the parents of the finer
 * level, dilated by one and octet-rounded, so every level fully covers the one below it plus a ring.
 * A level therefore has an INTERIOR (the volume a finer level also covers — the transient fallback
 * that keeps a region drawn while its children stream) and an OUTER RING (the volume only this level
 * covers — the far view, permanently). Both are wanted; the interior is nearer.
 *
 * THE ORDER IS FINE TO COARSE, and it is a display requirement, not an efficiency one: the world must
 * resolve outward from the camera, so the level nearest the eye finishes before the level behind it
 * starts. A level is served only once every finer level has nothing left to ask for.
 *
 * Two orderings have been tried and reported as bugs, so neither is available:
 *  - COARSEST-FIRST by level: "far coarse chunks display before the inner rings".
 *  - EQUAL SHARES between levels: every level advances in lockstep (measured l1 155/1331 against
 *    l2 152/729 after a pan), so the far ring appears alongside the near one instead of behind it —
 *    "lods are still loading out of order, on initial page load they should always display fine to
 *    coarse". A pooled distance sort is a third variant of the same mistake: it interleaves levels
 *    wherever their distance ranges overlap, which they always do (each coarse level covers the
 *    finer one plus a ring).
 *
 * lod 0 is not scheduled here at all — it streams through the base path on its own, larger window,
 * which is what puts the finest level first overall.
 *
 * THE DECIMATION STEP IS A PRIORITY TOO. `isStep` names the level a drawn coarse node is waiting
 * on to release; it leads the rest of the order so the staircase steps down one level at a time,
 * but it takes no slot away from a finer level that still has capacity — held shut instead, its own
 * deficit could never clear, because clearing it needs exactly the coverage it was forbidden.
 *
 * FINE-FIRST IS A PRIORITY, NOT AN EMBARGO. Strict "a level is served only when every finer level
 * has nothing left" starves the outer rings outright: measured in the harness, lod 2 drew NOTHING in
 * a 250-tick run — not late, never — because lod 1 never ran out of work, and column probes (which
 * gate a whole vertical stack each) doubled the effect. That is the reported "big gaps in the
 * terrain": the far view simply does not exist. So the finest level with work takes all but one
 * slot, and the leftover slot goes to the next level that has work. The far ring fills slowly rather
 * than never, and near terrain still gets the overwhelming majority of the window.
 *
 * Priority levels (the current base level and, mid-zoom, the level being stepped to) are served
 * before everything: a coarsening zoom step waits on that level being fully covered, so letting
 * anything else go first wedges the zoom ("scroll to zoom just doesn't load").
 *
 * NEAREST TO THE CENTRE OUTWARDS, AT EVERY LEVEL, WITHOUT EXCEPTION — see compareStreamOrder. A
 * shell completes before the next one opens, so a ring can never appear before the volume inside it.
 *
 * THIS REPLACES HOLE-FIRST, and the history is worth keeping because the failure was structural.
 * The old rule led with `uncovered` candidates — volume nothing displays — on the reasoning that a
 * hole is not refinement and a coarse chunk fills it 8^k times cheaper than the base level can. It
 * was self-limiting for interior volume (cover a hole and every finer candidate under it becomes
 * covered and drops back), but it was NOT self-limiting for the one class that matters most here: a
 * level's OUTER RING is permanently uncovered until it draws itself. So `uncovered` first meant, at
 * every coarse level, THE FAR RING BEFORE THE NEAR INTERIOR — permanently, by construction. Distance
 * only got a say once two candidates were in the same coverage class.
 *
 * That was survivable only because the far-view quota throttled the ring to a couple of consumer
 * groups per level per pass. Once the fine-first ladder made that quota redundant and it was deleted,
 * the brake went with it and the inversion ran free: coarse levels visibly filled from their outer
 * ring inwards. The fix is not to restore the brake — it is to stop needing one. Distance leads;
 * `uncovered` survives as a tiebreak between equally distant candidates, where "holes before
 * refinement" still holds and cannot invert anything.
 *
 * FINE TO COARSE BETWEEN LEVELS still stands, and inside what used to be the hole class it was a
 * measurement rather than an oversight. The overhaul plan specified coarse-first there ("coarse
 * covers holes with fewest requests") and the coverage harness rejected it, twice over, on a
 * 40-chunk pan (coverage.order.test.ts):
 *
 *  - IT STARVES THE LEVEL THAT CAN ACTUALLY TAKE THE VOLUME. With the coarsest uncovered level
 *    leading, lod 1 received ONE request in the pan's 40 ticks and none of the 18 lod-1 chunks
 *    covering the destination had arrived 60 ticks later.
 *  - THE COARSEST NODE IS THE LEAST LIKELY TO BE ALLOWED TO DRAW. No-steal (LodResidencyManager)
 *    withholds a coarse node while ANY descendant is on screen, and a lod-2 node's footprint is 64
 *    base chunks, so one early base chunk anywhere inside it blocks the cover permanently. Measured:
 *    all 8 lod-2 nodes over the destination were rendered at t468 and NONE was ever displayed.
 *
 * The cheapest cover that can actually appear wins — which is the finest level, nearest first.
 */

import { POSITIVE_MARGIN_OFFSETS_7 } from '@worldify/shared';

/**
 * THE NEAR-TO-FAR KEY FOR ONE LOADABLE POSITION: the nearest chunk that NEEDS it — itself, or any
 * chunk that reads it as a high-side margin source — identified both by distance and by which chunk
 * that is.
 *
 * WHY THE OWN DISTANCE IS THE WRONG KEY. A chunk cannot be drawn until all seven of its POSITIVE
 * margin sources have landed; a mesh built without one has an unbuilt boundary face and the render
 * gate refuses it. Those offsets all point the same way (+X/+Y/+Z and their diagonals), so ordering
 * by own distance makes the fill ASYMMETRIC: a chunk on the low side of the centre reads inward,
 * toward terrain that is nearer and therefore already here, while its mirror image on the high side
 * reads outward and waits for terrain nothing has asked for yet.
 *
 * Measured in the coverage harness at cold start, over positions the SAME distance from the centre —
 * images of one another under the cube's symmetry, so nothing about the world separates them:
 * `l1: -2,-2,-2 at t62 but 0,-1,2 at t133`, 71 ticks apart, with the same signature at every level
 * on both fixtures. Appearance time tracked the farthest SOURCE distance (r=0.98) more closely than
 * the chunk's own (r=0.95). On screen that is a whole octant of a ring lagging — the reported "gaps
 * in the corner of the rings still show but eventually fill correctly".
 *
 * `pre` THEN ORDERS A CHUNK'S SOURCES AHEAD OF THE CHUNK, and that is what finishes the job. Fixing
 * the distance alone still left the high side 26 ticks behind: a chunk on the low side reads inward,
 * so by the time its key class comes up its sources were long since fetched FOR THEIR OWN SAKE and
 * it is drawable the moment its own data lands, while its mirror image reads outward at chunks
 * nothing else wanted, which are only requested alongside it — it waits for the slowest of eight
 * instead of for one. Issuing the sources first within the class removes the difference: everything
 * at that distance then waits for the same thing. `group` keeps one chunk's neighbourhood contiguous
 * inside that, so a scarce window finishes chunks instead of almost-finishing many.
 *
 * @param isWanted  is this position IN RANGE (a consumer)? The load set's sacrificial +7 dilation is
 *                  never displayed itself, so it has no urgency of its own — only what it inherits.
 * @param pack      packs a consumer's coords into the stable `group` id.
 */
/**
 * THE NEAR-TO-FAR ORDER WITHIN A LEVEL: complete a SHELL, then start the next; nearest first inside
 * each shell. Returned as one number so it can be compared and minimised like a distance —
 * `shell * 4096 + squared distance`, both in that level's own chunk units, so the shell is the major
 * key and the distance breaks ties inside it. Integer-exact at any reachable radius.
 *
 * WHY NOT PLAIN EUCLIDEAN, which is what "nearest first" naively means. A level's in-range set is a
 * CUBE shell, and the eye reads it as a ring. Order the fill by pure distance and you grow a SPHERE
 * through that cube, so every shell's corners are served after the next shell's faces — measured in
 * the app at cold start, `lod 0: ring 3 corners drew at f235, after ring 4 faces at f234` and
 * `ring 4 corners at f265, after ring 5 faces at f237`, with the same signature at lod 1. On screen
 * that is a ring appearing with its corners cut out, which is the reported "the outer ring of chunks
 * and then the corner chunks still display far later".
 *
 * It is a real trade, not a free win: the corner of shell 4 is farther away (Euclidean) than the
 * face of shell 5, so this deliberately serves something farther first. The shell is the unit the
 * terrain is actually built and drawn in, so it is the unit that must complete.
 */
export function shellDistance(fx: number, fy: number, fz: number): number {
  const ax = Math.abs(fx), ay = Math.abs(fy), az = Math.abs(fz);
  const shell = Math.floor(Math.max(ax, ay, az));
  const d2 = fx * fx + fy * fy + fz * fz;
  return shell * 4096 + Math.min(d2, 4095);
}

export function marginSourceKey(
  x: number, y: number, z: number,
  distOf: (x: number, y: number, z: number) => number,
  isWanted: (x: number, y: number, z: number) => boolean,
  pack: (x: number, y: number, z: number) => number,
): { d: number; group: number; pre: number } {
  let d = distOf(x, y, z);
  let group = pack(x, y, z);
  let pre = 1; // nobody nearer needs it: this request is the chunk itself
  for (let i = 0; i < POSITIVE_MARGIN_OFFSETS_7.length; i++) {
    const off = POSITIVE_MARGIN_OFFSETS_7[i];
    const cx = x - off[0], cy = y - off[1], cz = z - off[2];
    if (!isWanted(cx, cy, cz)) continue;
    const cd = distOf(cx, cy, cz);
    if (cd < d) { d = cd; group = pack(cx, cy, cz); pre = 0; }
  }
  return { d, group, pre };
}

/**
 * The four columns a chunk's mesh depends on: its own, and the +X/+Z/+XZ neighbours its POSITIVE
 * margins read. A chunk cannot mesh complete until all four have arrived.
 */
export const COLUMN_LOOKAHEAD: ReadonlyArray<readonly [number, number]> = [[0, 0], [1, 0], [0, 1], [1, 1]];

/** A chunk that needs columns, as the base request sort produced it. */
export interface ColumnConsumer {
  cx: number;
  cz: number;
  /** The chunk's marginSourceKey-PROMOTED key — the one the chunk sort itself was ordered by. */
  d: number;
  /** The chunk's OWN shellDistance, unpromoted. */
  own: number;
}

/**
 * WHICH COLUMNS TO ASK FOR, IN WHAT ORDER. In local mode a tile request is really a COLUMN BUNDLE —
 * one worker job generating the tile and every surface chunk in the column together — so this list,
 * not the per-chunk requests, is what delivers the skin the player looks at. Its order IS the order
 * the ground appears in.
 *
 * A column takes the key of the NEAREST chunk that needs it, and the choice of "nearest" is the
 * whole subtlety. Two hops of look-ahead point the same way: `marginSourceKey` already promotes a
 * chunk to the distance of the nearest chunk that reads IT as a margin source (all seven offsets are
 * +X/+Y/+Z), and COLUMN_LOOKAHEAD then promotes the column again through +X/+Z. Keying by the
 * promoted `d` therefore compounds them, and the +X+Z quadrant gets fetched TWO shells early while
 * the −X−Z quadrant waits for its own:
 *
 *     column     own shell   promoted `d`   own `own`
 *     ( 3, 3)        3            1             2
 *     (−3,−3)        3            3             3
 *     ( 5, 5)        5            3             4
 *     (−5,−5)        5            5             5
 *
 * On screen that is the reported "more tiles display bottom left before top right, rather than an
 * even spread out from the central point".
 *
 * ONE hop is right, not zero. Modelling display as "a chunk draws when all four of its columns are
 * in", the four diagonal quadrants' mean display rank comes out:
 *
 *     promoted `d`   ++95   −−135   +−117   −+115    spread 40   (biased +X+Z)
 *     own `own`      ++111  −−117   +−115   −+114    spread  6
 *     column's own   ++134  −− 90   +−116   −+113    spread 43   (biased −X−Z)
 *
 * Dropping the look-ahead entirely just moves the asymmetry to the other quadrant: with POSITIVE-only
 * margins a +side chunk genuinely needs four columns it does not have, while a −side chunk's + sides
 * are already loaded. One shell of head start is the compensation the convention owes; two is the
 * bug. Hence `own` — the needing chunk's raw distance.
 *
 * Ties keep insertion order (a stable sort, deliberately no tie-break on the packed key): `chunks`
 * arrives in request order, which is already near-first, whereas ordering ties by packed column key
 * would sort them x-major and reintroduce a directional bias of its own.
 */
export function orderColumnsNeedingTiles(
  chunks: readonly ColumnConsumer[],
  packColumnKey: (cx: number, cz: number) => number,
  needsTile: (cx: number, cz: number) => boolean,
): number[] {
  const cols = new Map<number, number>();
  for (const chunk of chunks) {
    for (let i = 0; i < COLUMN_LOOKAHEAD.length; i++) {
      const x = chunk.cx + COLUMN_LOOKAHEAD[i][0], z = chunk.cz + COLUMN_LOOKAHEAD[i][1];
      if (!needsTile(x, z)) continue;
      const key = packColumnKey(x, z);
      const prev = cols.get(key);
      if (prev === undefined || chunk.own < prev) cols.set(key, chunk.own);
    }
  }
  return [...cols].sort((a, b) => a[1] - b[1]).map((e) => e[0]);
}

/**
 * All the scheduler needs of a request: which level it belongs to, and how far away it is. Chunk
 * candidates and column probes both satisfy it, and both need the same fairness — a level whose
 * columns are never probed cannot produce chunk candidates at all.
 */
export interface LodRequestCandidate {
  lod: number;
  /** shellDistance from the streaming centre, in THIS LEVEL'S chunk units — the near-to-far key
   *  within a level, and not comparable across levels (see compareStreamOrder). */
  d: number;
  /**
   * 0 when this request is a margin SOURCE for the chunk at distance `d`, 1 when it is that chunk
   * itself — sources go first, so a chunk is drawable as soon as its own data lands. See
   * marginSourceKey; without it the fill stays asymmetric even with `d` corrected.
   */
  pre?: number;
  /**
   * Which chunk this request is FOR (see marginSourceKey). Equally-distant requests are ordered by
   * it so one chunk's margin neighbourhood is issued as a block; without it a scarce window leaves
   * many chunks one source short of drawable instead of finishing a few.
   *
   */
  group?: number;
  /**
   * C5a — NOTHING ON SCREEN COVERS THIS REQUEST'S VOLUME: no displayed chunk at this position, no
   * displayed ancestor above it, no displayed descendant under it. A hole, in other words, judged
   * on the CONSUMER this request serves (a sacrificial margin source is never displayed itself and
   * has no coverage of its own — it inherits its consumer's, exactly as it inherits `d`).
   *
   * Undefined means "covered": every caller that does not model display coverage keeps the plain
   * fine-first order it always had.
   */
  uncovered?: boolean;
}


/**
 * THE COARSE LEVELS' BUDGET, in one place, because it is one budget in two halves and sizing either
 * half alone has produced a regression each time it was tried.
 *
 * A coarse column is fetched as a BUNDLE — one worker job returning the column's vertical clip AND
 * the chunks that clip admits — so the coarse levels' real claim on the shared terrain pool is
 * `coarseColumnWindow × MAX_BUNDLE_CHUNKS`, not the number of jobs in flight. Sizing the window as
 * if a column job were still a bare probe doubled that claim and the base level paid for it
 * (measured in the app: lod 0's first draw 7.5 s → 14.0 s, its 60-second count down 40%). The
 * product below is `genCount * 2`, which is `MAX_PENDING_LOD` — the chunk budget the coarse levels
 * always had, now spent in one round trip per column instead of two.
 */
export const MAX_BUNDLE_CHUNKS = 2;

/** MAX_PENDING_LOD_COLS: coarse column bundles in flight. See MAX_BUNDLE_CHUNKS for the sizing. */
export function coarseColumnWindow(genCount: number): number {
  return genCount;
}

/**
 * Slots left for the PER-CHUNK coarse path once the bundles in flight are counted. Both paths spend
 * the same window, so a bundle's payload has to be charged to it — otherwise the coarse levels
 * quietly hold twice their budget whenever both paths are busy.
 *
 * Zero is a normal answer, not a stall: the caller reports `deferred` and the streaming pump brings
 * the pass straight back (VoxelWorld.updateLodStreaming), so the rows a bundle's cap left behind go
 * out as soon as a column slot frees.
 */
export function coarseChunkSlots(
  maxLodInFlight: number, pendingChunks: number, pendingColumns: number,
): number {
  return maxLodInFlight - pendingChunks - pendingColumns * MAX_BUNDLE_CHUNKS;
}




/**
 * THE PER-CANDIDATE PAN DEBOUNCE (C5a). While the camera is moving, coarse REFINEMENT of volume
 * that is already drawn is work the pan invalidates a moment later — it waits for the settle, as
 * the whole coarse path used to. Work over UNCOVERED volume is the opposite: it is the blur that
 * makes fresh ground appear at all, and holding it for the debounce is precisely what produced
 * "a long empty void that pops straight to fine" (the debounce re-arms on every base-chunk
 * crossing, so a sustained pan blocked the coarse levels for its entire duration).
 */
export function passesPanDebounce(c: Pick<LodRequestCandidate, 'uncovered'>, panSettled: boolean): boolean {
  return panSettled || !!c.uncovered;
}

export interface LodRequestSchedule<T> {
  /** Requests to issue now, in order. */
  issue: T[];
  /** True when candidates were left unissued — the caller must keep the streaming pump armed. */
  deferred: boolean;
}


/**
 * THE STREAM ORDER — the one answer to "which position next", shared by every stage that has an
 * opinion. It used to be written out four times (the base requester, this scheduler, the mesh
 * dispatcher, the display commit), and three of those copies had drifted onto a plain squared
 * distance, which is not shell-major: a shell-3 corner sits at 27 and a shell-4 face at 16, so the
 * farther shell's face went first and each shell's corners were left as notches. Four stages
 * silently disagreeing about fill order is the class of bug this exists to make impossible.
 *
 *  - `lod` FIRST, because `d` is in each level's own chunk units and means nothing across levels.
 *  - `d` (shellDistance) next: a whole shell completes before the next one opens. THE rule —
 *    nearest to the centre outwards, never the reverse, or a ring appears before its interior.
 *  - `uncovered` only as a tiebreak at equal distance. It used to outrank both, which is what filled
 *    coarse levels from the far ring inwards: the ring is what nothing draws, so the ring is what
 *    classified uncovered, while the interior sat covered by the finer level and sorted last.
 *  - `pre`/`group` keep one chunk's margin neighbourhood together (see marginSourceKey).
 */
export function compareStreamOrder(a: LodRequestCandidate, b: LodRequestCandidate): number {
  if (a.lod !== b.lod) return a.lod - b.lod;
  if (a.d !== b.d) return a.d - b.d;
  const ua = a.uncovered ? 0 : 1;
  const ub = b.uncovered ? 0 : 1;
  if (ua !== ub) return ua - ub;
  if (a.pre !== b.pre) return (a.pre ?? 1) - (b.pre ?? 1);
  return (a.group ?? 0) - (b.group ?? 0);
}

/**
 * @param candidates  every requestable position, any order (grouped and sorted here).
 * @param slots       how many requests may be issued now (window minus what is already in flight).
 * @param isPriority  levels served before all others (the zoom's base level — see the header).
 * @param isStep      the level a drawn coarse node is being stepped down to — served first among
 *                    the rest, and only ordered: it keeps the held-back slot like any other class,
 *                    because a level that cannot acquire cannot clear the deficit that named it.
 */
export function scheduleLodRequests<T extends LodRequestCandidate>(
  candidates: readonly T[],
  slots: number,
  isPriority: (lod: number) => boolean,
  isStep: (lod: number) => boolean = () => false,
): LodRequestSchedule<T> {
  const issue: T[] = [];
  if (candidates.length === 0) return { issue, deferred: false };
  if (slots <= 0) return { issue, deferred: true };

  const order = [...candidates].sort((a, b) => {
    const pa = isPriority(a.lod) ? 0 : 1;
    const pb = isPriority(b.lod) ? 0 : 1;
    if (pa !== pb) return pa - pb;
    const sa = isStep(a.lod) ? 0 : 1;
    const sb = isStep(b.lod) ? 0 : 1;
    if (sa !== sb) return sa - sb;
    return compareStreamOrder(a, b);
  });

  // Priority levels take what they need, uncapped: a coarsening zoom step waits on the target level
  // being fully covered, so throttling it wedges the zoom.
  let i = 0;
  for (; i < order.length && issue.length < slots; i++) {
    if (!isPriority(order[i].lod)) break;
    issue.push(order[i]);
  }
  const rest = order.slice(i);
  if (rest.length === 0) return { issue, deferred: issue.length < order.length };

  // Fine-first for the remainder, with ONE slot held back for the next class so nothing is ever
  // fully embargoed (see the header — strict fine-first drew nothing at lod 2, ever). The CLASS is
  // the leading candidate's (uncovered, lod) pair: with no coverage information that is just the
  // finest level, unchanged; with it, hole-covering work leads and refinement keeps the held slot.
  const lead = rest[0];
  const leadUncovered = !!lead.uncovered;
  const sameClass = (c: T) => !!c.uncovered === leadUncovered && c.lod === lead.lod;
  const free = slots - issue.length;
  const holdBack = free >= 3 && rest.some((c) => !sameClass(c)) ? 1 : 0;
  const leadCap = free - holdBack;

  let led = 0;
  const others: T[] = [];
  for (const c of rest) {
    if (sameClass(c)) {
      if (led < leadCap) { issue.push(c); led++; }
    } else {
      others.push(c);
    }
    if (issue.length >= slots) break;
  }
  for (const c of others) {
    if (issue.length >= slots) break;
    issue.push(c);
  }
  return { issue, deferred: issue.length < order.length };
}
